"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  fadeUp,
  fadeScale,
  stagger,
  cardItem,
  slideLeft,
  slideRight,
  viewportOnce,
} from "../../components/motionVariants";
import ProductHeroCta from "@/app/components/ProductHeroCta";
import {
  BarChart2,
  Shield,
  Users,
  Package,
  Megaphone,
  ClipboardList,
  Link2,
  QrCode,
  Smartphone,
  Zap,
  Target,
  Phone,
  FileText,
  Map,
  MapPin,
  Trophy,
  Crosshair,
  Check,
} from "lucide-react";

// ─── Core Modules ─────────────────────────────────────────────────────
const modules = [
  {
    name: "Dashboard",
    Icon: BarChart2,
    desc: "Your entire sales operation on one screen. Live pipeline value, lead counts by stage, today's follow-up list, and top performers - all updated in real time.",
    highlights: [
      "Pipeline value by stage",
      "Follow-up task queue",
      "Team performance & top performers",
      "Sales performance by rep & stage",
    ],
  },
  {
    name: "Users & Roles",
    Icon: Shield,
    desc: "Create granular roles so your sales reps see only their leads, managers see the full team, and admins control everything. Works across CRM and Books.",
    highlights: [
      "Custom role creation",
      "Module-level permissions (View/Create/Edit/Delete)",
      "Team-wise data isolation",
      "Full audit log",
    ],
  },
  {
    name: "Customers & Leads",
    Icon: Users,
    desc: "Every customer and lead has a 360° profile - contact history, linked quotes, open orders, and a full activity timeline. No more digging through WhatsApp.",
    highlights: [
      "360° customer profile",
      "Lead status & priority tagging",
      "Activity timeline (calls, emails, meetings)",
      "Credit limit tracking",
    ],
  },
  {
    name: "Items",
    Icon: Package,
    desc: "Product and service catalogue synced directly from ABSS Books. When you create a quotation in CRM, pricing and GST rates are pulled automatically - no double entry.",
    highlights: [
      "Books Item Master sync",
      "HSN code & GST rate auto-fill",
      "Pricelist support per customer",
      "Item groups for sales reporting",
    ],
  },
  {
    name: "Campaigns & Leads",
    Icon: Megaphone,
    desc: "Launch targeted campaigns, capture leads from QR codes and web forms, and track exactly which campaign generated which lead. Attribution that actually works.",
    highlights: [
      "QR code lead capture",
      "Web form integration",
      "Campaign-to-lead attribution",
      "Follow-up task reminders",
    ],
  },
  {
    name: "Quotation & Sales Order",
    Icon: ClipboardList,
    desc: "Generate GST-compliant quotations in seconds. When the customer agrees, convert to a Sales Order with one click - which flows directly into ABSS Books without re-entry.",
    highlights: [
      "GST-inclusive & exclusive quotes",
      "Quote status tracking",
      "One-click Quote → Sales Order",
      "Books auto-sync on conversion",
    ],
  },
  {
    name: "Books Integration",
    Icon: Link2,
    desc: "CRM and Books share one data layer. Quotes become Sales Orders, which flow into Books ready to deliver and invoice. No manual handoff between teams.",
    highlights: [
      "Shared Item & Customer Master",
      "Quote → SO → Invoice chain",
      "Multi-branch & warehouse support",
    ],
  },
];

// ─── QR Lead Capture Flow ─────────────────────────────────────────────
const qrSteps = [
  {
    step: "01",
    Icon: QrCode,
    title: "At the Trade Show",
    desc: "Print your ABSS QR code on a standee or brochure. Visitor scans with any camera app - no install needed.",
  },
  {
    step: "02",
    Icon: Smartphone,
    title: "Visitor Fills Form",
    desc: "A mobile-optimised lead form opens instantly. Name, phone, business type, requirement - takes 30 seconds.",
  },
  {
    step: "03",
    Icon: Zap,
    title: "Lead Auto-Created",
    desc: "The lead appears in your CRM immediately. Tagged with the campaign, event name, date, and your sales rep's territory.",
  },
  {
    step: "04",
    Icon: Target,
    title: "Assigned to a Rep",
    desc: "The lead is assigned to the right sales rep by territory or category, and the rep gets an in-app notification.",
  },
  {
    step: "05",
    Icon: Phone,
    title: "Follow-Up Queued",
    desc: "A follow-up task is auto-created for the assigned rep - with a deadline. Nothing falls through the cracks.",
  },
  {
    step: "06",
    Icon: FileText,
    title: "Quote → Order",
    desc: "If interested, the rep generates a quotation in CRM. Customer approves - it becomes a Sales Order in Books.",
  },
];

// ─── Sales Funnel Stages ──────────────────────────────────────────────
const funnelStages = [
  {
    label: "New Leads",
    count: "100%",
    color: "#D4D4D4",
    desc: "All incoming leads from QR, web forms, manual entry, campaigns",
  },
  {
    label: "Qualified",
    count: "65%",
    color: "#A3A3A3",
    desc: "Leads that pass initial qualification - right fit, budget, timing",
  },
  {
    label: "Proposal Sent",
    count: "40%",
    color: "#FB923C",
    desc: "Quotation generated and shared with the prospect",
  },
  {
    label: "Negotiation",
    count: "25%",
    color: "#DF5D01",
    desc: "Price, terms, or scope being finalised with the customer",
  },
  {
    label: "Closed Won",
    count: "15%",
    color: "#171717",
    desc: "Sales Order confirmed - handed off to Books for invoicing",
  },
];

// ─── Geographic Intelligence ──────────────────────────────────────────
const geoFeatures = [
  {
    Icon: Map,
    title: "State-wise Lead Assignment",
    desc: "Give every state or city a clear owner. Assign leads to the rep who covers that region, so no enquiry sits unattended.",
  },
  {
    Icon: MapPin,
    title: "State-wise Reports",
    desc: "See leads and expo results broken down by state. Know where interest is strongest and where your team needs more coverage.",
  },
  {
    Icon: Trophy,
    title: "Area Performance Ranking",
    desc: "Compare sales performance across regions. Identify where your product sells fastest and where your team needs more coverage.",
  },
  {
    Icon: Crosshair,
    title: "Campaign Targeting by Region",
    desc: "Launch campaigns targeting specific geographies. QR lead forms capture location automatically - no manual tagging.",
  },
];

// ─── Testimonials ─────────────────────────────────────────────────────
const testimonials = [
  {
    name: "Shubham Patel",
    role: "Director, Om Sara Pvt. Ltd.",
    city: "Ahmedabad, Gujarat",
    quote:
      "PCB manufacturing means tracking every component that goes into a board. With ABSS, our production orders consume raw materials automatically, stock stays accurate, and finished boards are ready to invoice with E-Invoice in the same system. Sales enquiries come in through CRM and flow straight into Books.",
    metric: "Production to E-Invoice, one system",
  },
  {
    name: "Nikunj Patel",
    role: "Founder, Om Power Electronics Pvt. Ltd.",
    city: "Ahmedabad, Gujarat",
    quote:
      "Making stabilizers means buying a lot of components from many vendors and storing them across different warehouses. ABSS Books gives us the full purchase cycle, from PO to goods receipt to bill, and we can see exactly what stock is in which warehouse at any time. With CRM on the sales side, everything stays in one system.",
    metric: "Multi-warehouse stock, always accurate",
  },
  {
    name: "Sejal Dudhat",
    role: "Director, Victor Sales Impex Pvt. Ltd.",
    city: "Ahmedabad, Gujarat",
    quote:
      "Our sales team makes a lot of calls every day, and before ABSS we had no clear picture of who was following up with whom. Now every call is logged against the lead in ABSS CRM, so we can see each salesperson's activity and how many of their leads turn into orders. The orders then flow straight into Books.",
    metric: "Every call tracked, every lead followed up",
  },
];

export default function CRMPage() {
  const [activeModule, setActiveModule] = useState("Dashboard");
  const [activeFunnel, setActiveFunnel] = useState(0);

  const currentModule = modules.find((m) => m.name === activeModule);

  const [leadsOffset, setLeadsOffset] = useState(0);
  const [quotesOffset, setQuotesOffset] = useState(0);
  const [wonOffset, setWonOffset] = useState(0);
  const [crmLogs, setCrmLogs] = useState([
    { id: 1, text: "Lead scanned QR code (Mohit Sharma)" },
    { id: 2, text: "Quote sent: QTE-2026-921 (Sharma Traders) - ₹4.2L" },
    { id: 3, text: "Lead assigned to Rohit Sen" },
  ]);
  const [chartData, setChartData] = useState([
    40, 50, 45, 62, 59, 75, 80, 85, 82, 95,
  ]);

  useEffect(() => {
    const LOGS_LIST = [
      "Lead scanned QR code at Expo (Ramesh Patel)",
      "Visit logged: Patel Industries, Rajkot",
      "Lead assigned to Amit Roy",
      "Quotation QTE-2026-924 sent - ₹1.8L",
      "Deal Won: Nair Agri-Exports (Converted to Books SO)",
      "Follow-up task scheduled for rep Priya",
      "Call logged: Vikram Singh (Spoke for 7 mins)",
      "Lead status updated: Proposal Stage",
    ];

    const interval = setInterval(() => {
      setLeadsOffset((prev) => prev + (Math.floor(Math.random() * 3) - 1));
      setQuotesOffset((prev) => prev + Math.floor(Math.random() * 2));
      setWonOffset((prev) => prev + Math.floor(Math.random() * 12000) - 2000);

      setChartData((prev) => {
        const nextVal = Math.max(
          10,
          Math.min(
            100,
            prev[prev.length - 1] + (Math.floor(Math.random() * 21) - 10),
          ),
        );
        return [...prev.slice(1), nextVal];
      });

      setCrmLogs((prev) => {
        const randomLog =
          LOGS_LIST[Math.floor(Math.random() * LOGS_LIST.length)];
        return [{ id: Date.now(), text: randomLog }, ...prev.slice(0, 2)];
      });
    }, 3500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white overflow-x-hidden w-full">
      {/* ── HERO ───────────────────────────────────────────────────── */}
      <section className="relative pt-28 sm:pt-14 pb-16 sm:pb-20 bg-white border-b border-[#E5E5E5] overflow-hidden">
        {/* Ambient background - same treatment as the homepage hero */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div className="relative z-10 max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center min-w-0">
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="min-w-0 w-full flex flex-col items-center text-center md:items-start md:text-left"
            >
              <motion.div variants={fadeUp} className="mb-5 flex justify-center md:justify-start">
                <span className="inline-flex items-center gap-1.5 border border-[#E5E5E5] rounded-full px-3.5 sm:px-4 py-1.5 text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse" />
                  ABSS CRM
                </span>
              </motion.div>
              <motion.h1
                variants={fadeUp}
                className="sentient-bold text-[28px] sm:text-[48px] lg:text-[54px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-5 sm:mb-6 tracking-tight"
              >
                Empower Your Sales Team to Close More - With Less Chaos
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="geist-medium text-[15px] sm:text-lg leading-relaxed text-[#525252] mb-7 sm:mb-8 max-w-xl"
              >
                ABSS CRM centralises your leads, campaigns, quotations, and
                customer orders in one platform. QR capture at trade shows,
                state-wise sales insights, and real-time Books sync - built for
                Indian SME sales teams.
              </motion.p>
              <motion.div
                variants={fadeUp}
                className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center md:justify-start gap-3 sm:gap-4 w-full max-w-[320px] sm:max-w-none"
              >
                <ProductHeroCta app="CRM" demoLabel="Book a CRM Demo" />
                <a
                  href="#modules"
                  className="inline-flex items-center justify-center gap-2 h-[46px] sm:h-[48px] px-8 w-full sm:w-auto border border-[#E5E5E5] bg-white/80 backdrop-blur-sm text-[#262626] geist-semibold text-sm sm:text-base rounded-lg hover:bg-neutral-50 hover:text-black transition-all duration-300"
                >
                  Explore Modules ↓
                </a>
              </motion.div>

              {/* Trust pills */}
              <motion.div
                variants={fadeUp}
                className="flex flex-wrap justify-center md:justify-start gap-2 sm:gap-2.5 mt-7 sm:mt-8"
              >
                {[
                  "QR Lead Capture",
                  "State-wise Insights",
                  "Books Integrated",
                  "GST-Ready",
                ].map((label, i) => (
                  <span
                    key={i}
                    className="border border-[#E5E5E5] rounded-full px-3.5 py-1.5 text-xs sm:text-[13px] text-[#262626] geist-medium bg-white/70 backdrop-blur-sm shadow-sm hover:border-[#df5d01]/30 transition-all"
                  >
                    {label}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* Dashboard preview */}
            <motion.div
              variants={fadeScale}
              initial="hidden"
              animate="show"
              className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-[16px] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.08)] min-w-0 w-full"
            >
              {/* Mock Browser Header */}
              <div className="bg-[#FAFAFA] border-b border-[#E5E5E5] px-3 sm:px-4 py-3 sm:py-3.5 flex items-center gap-2 sm:gap-3 min-w-0">
                <div className="flex gap-1.5 shrink-0">
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E5E5E5] block" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E5E5E5] block" />
                  <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-[#E5E5E5] block" />
                </div>
                <div className="flex-1 min-w-0 max-w-[280px] bg-white border border-[#E5E5E5] rounded-full py-1 px-2.5 sm:px-4 flex items-center gap-1.5 text-[10px] sm:text-xs text-[#737373] font-mono">
                  <span className="text-[#A3A3A3] shrink-0">https://</span>
                  <span className="truncate">crm.abss.ai/dashboard</span>
                </div>
                <div className="ml-auto hidden sm:flex items-center gap-1.5 bg-[#FFF7ED] text-[#DF5D01] px-2 sm:px-3 py-1 rounded-full text-[9px] sm:text-[10px] geist-bold uppercase tracking-wider border border-orange-100 shrink-0">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] inline-block animate-ping" />
                  Live Sync
                </div>
              </div>

              <div className="p-3.5 sm:p-6 space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between mb-1 gap-2 min-w-0">
                  <span className="geist-bold text-sm text-[#171717] truncate">
                    ABSS CRM Dashboard
                  </span>
                  <span className="text-[10px] text-[#A3A3A3] bg-[#F5F5F5] px-2 py-0.5 rounded geist-semibold uppercase tracking-wider shrink-0">
                    Live Updates
                  </span>
                </div>

                {/* KPI Row */}
                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                  {[
                    {
                      label: "Open Leads",
                      val: 142 + leadsOffset,
                      color: "#171717",
                    },
                    {
                      label: "Quotes Sent",
                      val: 38 + quotesOffset,
                      color: "#171717",
                    },
                    {
                      label: "Won This Month",
                      val:
                        "₹" + ((1240000 + wonOffset) / 100000).toFixed(2) + "L",
                      color: "#DF5D01",
                    },
                  ].map((kpi, i) => (
                    <div
                      key={i}
                      className="bg-[#FAFAFA] border border-[#E5E5E5] p-2.5 sm:p-4 rounded-[10px] sm:rounded-[12px] transition-all duration-300 min-w-0"
                    >
                      <div
                        className="sentient-bold text-base sm:text-2xl transition-colors duration-500 tracking-tight truncate"
                        style={{ color: kpi.color }}
                      >
                        {kpi.val}
                      </div>
                      <div className="geist-semibold text-[9px] sm:text-[10px] text-[#737373] mt-1 sm:mt-1.5 uppercase tracking-wider leading-tight">
                        {kpi.label}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Live Real-time Sparkline Graph */}
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 rounded-[12px]">
                  <div className="flex justify-between items-center mb-3">
                    <span className="geist-bold text-[11px] text-[#737373] uppercase tracking-wider">
                      Lead Acquisition Velocity
                    </span>
                    <span className="text-[10px] text-[#DF5D01] geist-semibold flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] animate-ping" />
                      Live Stream
                    </span>
                  </div>
                  <div className="h-16 flex items-end">
                    <svg
                      className="w-full h-full text-[#DF5D01]"
                      viewBox="0 0 100 30"
                      preserveAspectRatio="none"
                    >
                      <defs>
                        <linearGradient
                          id="crmChartGrad"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="0%"
                            stopColor="#DF5D01"
                            stopOpacity="0.25"
                          />
                          <stop
                            offset="100%"
                            stopColor="#DF5D01"
                            stopOpacity="0"
                          />
                        </linearGradient>
                      </defs>
                      <path
                        d={`M 0 30 L ${chartData.map((val, idx) => `${idx * 11.1} ${30 - (val / 100) * 25}`).join(" L ")} L 100 30 Z`}
                        fill="url(#crmChartGrad)"
                        className="transition-all duration-500 ease-in-out"
                      />
                      <path
                        d={`M ${chartData.map((val, idx) => `${idx * 11.1} ${30 - (val / 100) * 25}`).join(" L ")}`}
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="transition-all duration-500 ease-in-out"
                      />
                    </svg>
                  </div>
                </div>

                {/* Pipeline bars */}
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 rounded-[12px]">
                  <div className="geist-bold text-[11px] text-[#737373] uppercase tracking-wider mb-3">
                    Pipeline by Stage
                  </div>
                  {[
                    { stage: "New Leads", pct: 100, color: "#D4D4D4" },
                    {
                      stage: "Qualified",
                      pct: Math.min(100, Math.max(0, 65 + leadsOffset)),
                      color: "#A3A3A3",
                    },
                    {
                      stage: "Proposal",
                      pct: Math.min(100, Math.max(0, 40 + quotesOffset)),
                      color: "#FB923C",
                    },
                    { stage: "Won", pct: 15, color: "#DF5D01" },
                  ].map((bar, i) => (
                    <div key={i} className="mb-3.5 last:mb-0">
                      <div className="flex justify-between mb-1.5">
                        <span className="geist-semibold text-xs text-[#525252]">
                          {bar.stage}
                        </span>
                        <span className="geist-bold text-xs text-[#171717] transition-all duration-500">
                          {bar.pct}%
                        </span>
                      </div>
                      <div className="h-2 bg-[#E5E5E5]/70 w-full rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-500 ease-out rounded-full"
                          style={{
                            width: `${bar.pct}%`,
                            backgroundColor: bar.color,
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Live CRM Activity Log Ticker */}
                <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-4 rounded-[12px]">
                  <div className="geist-bold text-[11px] text-[#737373] uppercase tracking-wider mb-3 flex items-center justify-between">
                    <span>Live CRM Activity Ticker</span>
                    <span className="text-[9px] text-[#DF5D01] bg-[#FFF7ED] border border-orange-100 px-2 py-0.5 rounded-full uppercase geist-bold">
                      Streaming
                    </span>
                  </div>
                  <div className="space-y-2 h-[128px] overflow-hidden relative">
                    <AnimatePresence initial={false}>
                      {crmLogs.map((log) => (
                        <motion.div
                          key={log.id}
                          initial={{ opacity: 0, y: -20, height: 0 }}
                          animate={{ opacity: 1, y: 0, height: "auto" }}
                          exit={{ opacity: 0, y: 20, height: 0 }}
                          transition={{
                            type: "spring",
                            stiffness: 500,
                            damping: 30,
                          }}
                          className="flex items-center gap-2.5 py-2.5 border-b border-[#E5E5E5]/60 last:border-0 overflow-hidden"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] flex-shrink-0 animate-pulse" />
                          <span className="geist-semibold text-xs text-[#404040] leading-snug flex-1">
                            {log.text}
                          </span>
                          <span className="text-[10px] text-[#A3A3A3] font-mono whitespace-nowrap">
                            Just now
                          </span>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────────────── */}
      <section className="border-b border-[#E5E5E5] bg-white">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="grid grid-cols-2 md:grid-cols-4 md:divide-x divide-[#E5E5E5]"
            variants={stagger}
            {...viewportOnce}
          >
            {[
              { num: "7", label: "CRM Modules" },
              { num: "< 60 sec", label: "QR Scan to Lead" },
              { num: "1-Click", label: "Quote to Sales Order" },
              { num: "100%", label: "Cloud - Any Device, Anywhere" },
            ].map((s, i) => (
              <motion.div
                key={i}
                variants={fadeUp}
                className="px-3 sm:px-6 md:px-8 py-6 sm:py-8 text-center"
              >
                <div className="sentient-medium text-3xl sm:text-4xl text-[#DF5D01] mb-1">
                  {s.num}
                </div>
                <div className="geist-semibold text-[10px] sm:text-xs uppercase tracking-wider text-[#A3A3A3] leading-snug">
                  {s.label}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── CORE MODULES ──────────────────────────────────────────── */}
      <section id="modules" className="py-16 sm:py-24 bg-white">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="mb-10 sm:mb-14 text-center md:text-left flex flex-col items-center md:items-start"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              Core Modules
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              Everything Your Sales Team Needs in One Place
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-2xl leading-relaxed"
            >
              Seven interconnected modules - from lead capture to books
              integration. Click any module to explore what it does.
            </motion.p>
          </motion.div>

          {/* Module tab pills */}
          <motion.div
            variants={fadeUp}
            {...viewportOnce}
            className="flex flex-nowrap md:flex-wrap justify-center md:justify-start gap-2 mb-6 overflow-x-auto scrollbar-hide -mx-5 px-5 sm:-mx-6 sm:px-6 md:mx-0 md:px-0 pb-1"
          >
            {modules.map((mod) => (
              <button
                key={mod.name}
                onClick={() => setActiveModule(mod.name)}
                className={`flex items-center gap-2 px-4 h-10 rounded-lg border geist-semibold text-[13px] whitespace-nowrap transition-all duration-200 cursor-pointer flex-shrink-0 ${
                  activeModule === mod.name
                    ? "bg-[#171717] border-[#171717] text-white shadow-md"
                    : "bg-white border-[#E5E5E5] text-[#525252] hover:border-[#D4D4D4] hover:text-[#171717]"
                }`}
              >
                <mod.Icon className="w-4 h-4" />
                {mod.name}
              </button>
            ))}
          </motion.div>

          {/* Detail card */}
          <AnimatePresence mode="wait">
            <motion.div
              key={activeModule}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="border border-[#E5E5E5] rounded-xl sm:rounded-[16px] bg-[#FAFAFA] p-4 sm:p-9 grid md:grid-cols-[1fr_1.15fr] gap-6 sm:gap-8 md:gap-12 items-start min-w-0"
            >
              {/* Left: title + description */}
              <div className="text-center md:text-left flex flex-col items-center md:items-start">
                <div className="flex items-center justify-center md:justify-start gap-3.5 mb-5">
                  {currentModule && (
                    <div className="w-11 h-11 rounded-[12px] bg-[#171717] text-white flex items-center justify-center flex-shrink-0">
                      <currentModule.Icon className="w-5 h-5" />
                    </div>
                  )}
                  <span className="sentient-medium text-[22px] sm:text-2xl text-[#171717] leading-tight">
                    {currentModule?.name}
                  </span>
                </div>
                <p className="geist-regular text-[#525252] text-[15px] leading-relaxed">
                  {currentModule?.desc}
                </p>
              </div>

              {/* Right: capabilities grid */}
              <div className="text-center md:text-left">
                <p className="geist-semibold text-[10px] tracking-widest text-[#DF5D01] uppercase mb-4">
                  Key Capabilities
                </p>
                <div className="grid sm:grid-cols-2 gap-2.5">
                  {currentModule?.highlights.map((h, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.04 }}
                      className="bg-white border border-[#E5E5E5] rounded-[10px] px-4 py-3 flex items-center gap-3"
                    >
                      <Check
                        className="w-4 h-4 text-[#DF5D01] flex-shrink-0"
                        strokeWidth={3}
                      />
                      <span className="geist-medium text-[13px] text-[#404040] leading-snug">
                        {h}
                      </span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── QR LEAD CAPTURE FLOW ──────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-16"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              QR Lead Capture
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              Never Lose a Lead at a Trade Show Again
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-xl mx-auto leading-relaxed"
            >
              From visitor scanning your QR code to a qualified lead in your CRM
              - in under 60 seconds. No paper forms. No manual entry. No
              excuses.
            </motion.p>
          </motion.div>

          {/* Flow Steps - card grid */}
          <div className="relative">
            {/* Connector line */}
            <div className="hidden lg:block absolute top-10 left-[calc(16.66%+20px)] right-[calc(16.66%+20px)] h-px bg-[#E5E5E5] z-0" />

            <motion.div
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 relative z-10"
              variants={stagger}
              {...viewportOnce}
            >
              {qrSteps.map((step, i) => (
                <motion.div
                  key={i}
                  variants={cardItem}
                  className="bg-white border border-[#E5E5E5] hover:border-[#D4D4D4] rounded-[12px] p-4 sm:p-6 shadow-sm hover:shadow-md transition-all duration-300 min-w-0"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 bg-[#171717] text-white geist-bold text-xs rounded-full flex items-center justify-center flex-shrink-0">
                      {step.step}
                    </div>
                    <div className="w-10 h-10 bg-[#F5F5F5] rounded-[10px] flex items-center justify-center text-[#DF5D01] flex-shrink-0">
                      <step.Icon className="w-5 h-5" />
                    </div>
                  </div>
                  <h3 className="sentient-medium text-base text-[#171717] mb-2">
                    {step.title}
                  </h3>
                  <p className="geist-regular text-[13px] text-[#525252] leading-relaxed">
                    {step.desc}
                  </p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── SALES FUNNEL ──────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center min-w-0">
            {/* Left: Text */}
            <motion.div
              variants={stagger}
              {...viewportOnce}
              className="min-w-0 w-full flex flex-col items-center text-center md:items-start md:text-left"
            >
              <motion.p
                variants={fadeUp}
                className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
              >
                Sales Pipeline
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-6 tracking-tight"
              >
                See Exactly Where Every Deal Stands
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="geist-regular text-base sm:text-lg text-[#525252] leading-relaxed mb-8 max-w-xl"
              >
                ABSS CRM shows your sales funnel in real time, built on the lead
                stages you define. See how many leads move from one stage to the
                next, spot where deals get stuck, and fix the bottleneck - not
                the symptom. The funnel below is an example.
              </motion.p>
              <motion.div variants={fadeUp} className="space-y-2.5 w-full">
                {funnelStages.map((stage, i) => {
                  const isCurrentFunnel = activeFunnel === i;
                  return (
                    <button
                      key={i}
                      onClick={() => setActiveFunnel(i)}
                      className={`w-full flex items-center gap-3 sm:gap-4 p-3 sm:p-4 border text-left transition-all duration-200 rounded-[12px] cursor-pointer min-w-0 ${
                        isCurrentFunnel
                          ? "border-[#DF5D01]/40 bg-[#FFF7ED]/40"
                          : "border-[#E5E5E5] bg-white hover:bg-[#FAFAFA]"
                      }`}
                    >
                      <div
                        className="w-3 h-3 rounded-full flex-shrink-0"
                        style={{
                          backgroundColor: isCurrentFunnel
                            ? "#DF5D01"
                            : stage.color,
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <span
                          className={`geist-semibold text-sm ${isCurrentFunnel ? "text-[#171717]" : "text-[#525252]"}`}
                        >
                          {stage.label}
                        </span>
                      </div>
                      <span
                        className={`sentient-medium text-base sm:text-lg shrink-0 ${isCurrentFunnel ? "text-[#DF5D01]" : "text-[#737373]"}`}
                      >
                        {stage.count}
                      </span>
                    </button>
                  );
                })}
              </motion.div>
            </motion.div>

            {/* Right: Funnel Visual */}
            <motion.div variants={slideLeft} {...viewportOnce}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeFunnel}
                  initial={{ opacity: 0, scale: 0.97 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.3 }}
                  className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl sm:rounded-[16px] p-5 sm:p-10 min-w-0"
                >
                  {/* Funnel shape */}
                  <div className="space-y-2 mb-8">
                    {funnelStages.map((stage, i) => (
                      <div
                        key={i}
                        className="h-10 flex items-center justify-center geist-bold text-xs text-white transition-all duration-500 rounded-md"
                        style={{
                          backgroundColor:
                            i === activeFunnel ? "#DF5D01" : stage.color,
                          width: stage.count,
                          maxWidth: "100%",
                          margin: "0 auto",
                          opacity: i === activeFunnel ? 1 : 0.3,
                        }}
                      >
                        {i === activeFunnel &&
                          `${stage.label} - ${stage.count}`}
                      </div>
                    ))}
                  </div>
                  {/* Active stage detail */}
                  <div className="border-t border-[#E5E5E5] pt-6">
                    <h3 className="sentient-medium text-2xl mb-2 text-[#171717]">
                      {funnelStages[activeFunnel].label}
                    </h3>
                    <p className="geist-regular text-sm text-[#525252] leading-relaxed">
                      {funnelStages[activeFunnel].desc}
                    </p>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── GEOGRAPHIC INTELLIGENCE ───────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-14"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              Geographic Intelligence
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              India Has 28 States. Your CRM Should Know Them All.
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="geist-regular text-base sm:text-lg text-[#525252] max-w-2xl mx-auto leading-relaxed"
            >
              ABSS CRM is built for businesses that sell across Indian
              geographies. Territory management, region-wise pipeline, and
              location-based lead routing - all without a spreadsheet.
            </motion.p>
          </motion.div>

          <motion.div
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4"
            variants={stagger}
            {...viewportOnce}
          >
            {geoFeatures.map((f, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="bg-white border border-[#E5E5E5] hover:border-[#D4D4D4] rounded-xl sm:rounded-[16px] p-5 sm:p-7 shadow-sm hover:shadow-md transition-all duration-300 min-w-0"
              >
                <div className="w-10 h-10 rounded-[10px] bg-[#F5F5F5] flex items-center justify-center mb-5 text-[#DF5D01]">
                  <f.Icon className="w-5 h-5" strokeWidth={2} />
                </div>
                <h3 className="sentient-medium text-xl text-[#171717] mb-3">
                  {f.title}
                </h3>
                <p className="geist-regular text-sm text-[#525252] leading-relaxed">
                  {f.desc}
                </p>
              </motion.div>
            ))}
          </motion.div>

          {/* India map representation */}
          <motion.div
            variants={fadeUp}
            {...viewportOnce}
            className="mt-10 sm:mt-12 bg-white border border-[#E5E5E5] rounded-xl sm:rounded-[16px] p-5 sm:p-10 text-center min-w-0"
          >
            <p className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-6">
              Typical Territory Setup for Indian SMEs
            </p>
            <div className="flex flex-wrap justify-center gap-2.5">
              {[
                "Maharashtra",
                "Gujarat",
                "Rajasthan",
                "Delhi/NCR",
                "Tamil Nadu",
                "Karnataka",
                "Telangana",
                "West Bengal",
                "Punjab",
                "Uttar Pradesh",
                "Madhya Pradesh",
                "Haryana",
              ].map((state, i) => (
                <motion.span
                  key={i}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.04 }}
                  className="px-4 py-2 bg-[#FAFAFA] border border-[#E5E5E5] geist-medium text-xs text-[#525252] rounded-full hover:bg-[#F5F5F5] transition-colors"
                >
                  {state}
                </motion.span>
              ))}
              <span className="px-4 py-2 bg-[#FFF7ED] border border-orange-100 geist-semibold text-xs text-[#DF5D01] rounded-full">
                + 16 more states
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── BOOKS INTEGRATION ─────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-white border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <div className="grid md:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center min-w-0">
            {/* Left: Visual */}
            <motion.div variants={slideRight} {...viewportOnce} className="min-w-0">
              <div className="bg-[#FAFAFA] border border-[#E5E5E5] rounded-xl sm:rounded-[16px] p-4 sm:p-8 min-w-0">
                <p className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-6">
                  Data Flow - CRM to Books
                </p>
                {[
                  {
                    from: "CRM: Lead Created",
                    to: "→",
                    status: "automatic",
                  },
                  {
                    from: "CRM: Quotation Sent",
                    to: "→",
                    status: "no re-entry",
                  },
                  {
                    from: "CRM: Quote Accepted",
                    to: "→",
                    status: "auto-converts",
                  },
                  {
                    from: "Books: Sales Order",
                    to: "→",
                    status: "auto-created",
                  },
                  {
                    from: "Books: Invoice Generated",
                    to: "→",
                    status: "IRN auto-gen",
                  },
                ].map((row, i) => (
                  <div
                    key={i}
                    className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 py-3 sm:py-3.5 border-b border-[#E5E5E5]/60 last:border-0 min-w-0"
                  >
                    <span className="geist-semibold text-[13px] sm:text-sm text-[#404040] flex-1 min-w-0">
                      {row.from}
                    </span>
                    <span className="text-[#A3A3A3] hidden sm:inline">{row.to}</span>
                    <span className="geist-semibold text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full flex-shrink-0 bg-[#FFF7ED] text-[#DF5D01] border border-orange-100">
                      {row.status}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right: Text */}
            <motion.div
              variants={stagger}
              {...viewportOnce}
              className="min-w-0 w-full flex flex-col items-center text-center md:items-start md:text-left"
            >
              <motion.p
                variants={fadeUp}
                className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
              >
                CRM + Books Integration
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-6 tracking-tight"
              >
                One Sale. Zero Double Entry.
              </motion.h2>
              <motion.p
                variants={fadeUp}
                className="geist-regular text-base sm:text-lg text-[#525252] leading-relaxed mb-6 max-w-xl"
              >
                When your sales rep converts a quote to a Sales Order in CRM,
                ABSS Books automatically creates the corresponding record. Your
                accounts team never needs to wait for a WhatsApp message or an
                Excel file.
              </motion.p>
              <motion.div variants={stagger} className="space-y-4 w-full max-w-xl md:max-w-none">
                {[
                  "Shared Item Master - pricing and HSN codes always in sync",
                  "Customer credit limits visible in CRM before sending a quote",
                  "Multi-branch support - each branch can have its own pipeline",
                ].map((point, i) => (
                  <motion.div
                    key={i}
                    variants={fadeUp}
                    className="flex items-start gap-3 text-left"
                  >
                    <Check
                      className="w-4 h-4 text-[#DF5D01] flex-shrink-0 mt-1"
                      strokeWidth={3}
                    />
                    <span className="geist-medium text-sm text-[#404040] leading-relaxed">
                      {point}
                    </span>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ──────────────────────────────────────────── */}
      <section className="py-16 sm:py-24 bg-[#FAFAFA] border-t border-[#E5E5E5]">
        <div className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12">
          <motion.div
            className="text-center mb-10 sm:mb-14"
            variants={stagger}
            {...viewportOnce}
          >
            <motion.p
              variants={fadeUp}
              className="geist-medium text-xs tracking-widest text-[#DF5D01] uppercase mb-3.5"
            >
              Customer Stories
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="sentient-medium text-[24px] sm:text-[40px] leading-tight text-[#171717] mb-4 tracking-tight"
            >
              What Indian SMEs Say About ABSS CRM
            </motion.h2>
          </motion.div>

          <motion.div
            className="grid md:grid-cols-3 gap-4"
            variants={stagger}
            {...viewportOnce}
          >
            {testimonials.map((t, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-[16px] p-5 sm:p-7 flex flex-col shadow-sm hover:shadow-md transition-all duration-300 min-w-0"
              >
                <p className="geist-regular text-[#525252] text-sm leading-relaxed mb-6 flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="border-t border-[#F5F5F5] pt-5 flex items-center gap-4">
                  <div className="w-11 h-11 rounded-full bg-[#F5F5F5] border border-[#E5E5E5] flex items-center justify-center text-sm geist-bold text-[#171717] flex-shrink-0">
                    {t.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </div>
                  <div className="flex-1">
                    <div className="geist-semibold text-sm text-[#171717]">
                      {t.name}
                    </div>
                    <div className="geist-medium text-xs text-[#A3A3A3] mt-0.5">
                      {t.role}
                    </div>
                    <div className="geist-medium text-[11px] text-[#A3A3A3] mt-0.5">
                      {t.city}
                    </div>
                  </div>
                </div>
                <div className="mt-4 inline-flex items-center justify-center geist-semibold text-xs text-[#DF5D01] bg-[#FFF7ED] border border-orange-100 px-3 py-1 rounded-full self-start">
                  {t.metric}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
    </div>
  );
}
